import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { PreReleaseProject, preReleaseService } from '../../services/preReleaseService';
import ProjectUpdateConfirmDialog from '../../components/prerelease/ProjectUpdateConfirmDialog';
import PricingCalculatorDisplay from '../../components/projects/PricingCalculatorDisplay';
import { PricingValidationService, PricingCalculation } from '../../services/pricingValidationService';
import ProjectCreationRequirements from '../../components/projects/ProjectCreationRequirements';
import { useProjectCreationEligibility } from '../../hooks/useProjectCreationSettings';
import { JourneyPlatformIntegrationService } from '../../services/journeyPlatformIntegrationService';
import { JourneyBackButton } from '../../components/journey/JourneyBackButton';
import '../../styles/pages/CreatePreReleaseProjectPage.css';
import '../../styles/components/prerelease/PreReleaseModals.css';
import '../../styles/components/PricingCalculatorDisplay.css';

interface FormState {
  // Step 1: Basic Information
  title: string;
  project_type: 'app' | 'website' | 'product' | 'service' | 'other';
  description: string;
  concept_details: string;

  // Step 2: Project Details
  key_features: Array<{ text: string; screenshot: File | null; existingScreenshotUrl?: string }>;
  proposed_price: string;
  early_bird_discount: string;
  estimated_timeline: string;
  project_category: string;

  // Step 3: Media & Links
  image_url: string;
  demo_url: string;
}

interface ValidationError {
  field: string;
  message: string;
  type: 'required' | 'format' | 'length' | 'file' | 'business';
}

interface UIState {
  currentStep: number;
  activeTab: 'basic' | 'details' | 'media' | 'review';
  loading: boolean;
  submitting: boolean;
  uploadingFiles: boolean;
  error: string | null;
  errorType?: 'network' | 'validation' | 'permission' | 'storage' | 'unknown';
  recoveryActions?: string[];
  success: string | null;
  validationErrors: ValidationError[];
  fieldErrors: Record<string, string>;
  isBasicComplete: boolean;
  isDetailsComplete: boolean;
  isMediaComplete: boolean;
  mode: 'create' | 'edit';
  originalProject?: PreReleaseProject;
  hasUnsavedChanges: boolean;
  showValidationSummary: boolean;
  confirmDialog: {
    isOpen: boolean;
    type: 'priceChange' | 'unsavedChanges' | 'sensitiveChange';
    title: string;
    message: string;
    details?: string;
    confirmText?: string;
    cancelText?: string;
    onConfirm: () => void;
    warningLevel?: 'info' | 'warning' | 'danger';
  } | null;
}

const CreatePreReleaseProjectPage: React.FC = () => {
  const { user } = useAuth();
  const { t, uiLanguage } = useLanguage();
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // Determine mode based on URL parameters
  const isEditMode = projectId !== undefined;

  // Project creation eligibility check
  const {
    eligibility,
    loading: eligibilityLoading,
    error: eligibilityError,
    refetch: refetchEligibility,
    canCreate
  } = useProjectCreationEligibility();

  // Form state
  const [formData, setFormData] = useState<FormState>({
    title: '',
    project_type: 'app',
    description: '',
    concept_details: '',
    key_features: [{ text: '', screenshot: null, existingScreenshotUrl: undefined }],
    proposed_price: '',
    early_bird_discount: '',
    estimated_timeline: '',
    project_category: '',
    image_url: '',
    demo_url: ''
  });

  // UI state
  const [uiState, setUIState] = useState<UIState>({
    currentStep: 1,
    activeTab: 'basic',
    loading: false,
    submitting: false,
    uploadingFiles: false,
    error: null,
    success: null,
    validationErrors: [],
    fieldErrors: {},
    isBasicComplete: false,
    isDetailsComplete: false,
    isMediaComplete: false, // Will be calculated by validation
    mode: isEditMode ? 'edit' : 'create',
    hasUnsavedChanges: false,
    showValidationSummary: false,
    confirmDialog: null
  });

  // Pricing calculation state
  const [pricingCalculation, setPricingCalculation] = useState<PricingCalculation | undefined>();

  // Load draft functionality
  const loadDraft = useCallback(() => {
    try {
      const draftJson = localStorage.getItem('prerelease-project-draft');
      if (draftJson) {
        const draft = JSON.parse(draftJson);
        // Only load draft if it's for the same mode and project
        if (draft.mode === (isEditMode ? 'edit' : 'create') &&
          draft.projectId === (projectId || null)) {
          return draft;
        }
      }
    } catch (error) {
      console.warn('Failed to load draft:', error);
    }
    return null;
  }, [isEditMode, projectId]);

  // Clear draft functionality
  const clearDraft = useCallback(() => {
    try {
      localStorage.removeItem('prerelease-project-draft');
    } catch (error) {
      console.warn('Failed to clear draft:', error);
    }
  }, []);

  // Load project data for edit mode or draft for create mode
  useEffect(() => {
    if (isEditMode && projectId) {
      loadProjectForEdit(projectId);
    } else {
      // Check for AI-generated data first (from journey flow)
      const journeyId = searchParams.get('journeyId');
      if (journeyId) {
        const preReleaseKey = `ai_pre_release_data_${journeyId}`;
        const aiGeneratedData = sessionStorage.getItem(preReleaseKey);

        if (aiGeneratedData) {
          try {
            const parsedData = JSON.parse(aiGeneratedData);
            console.log('✅ Found AI-generated pre-release data:', parsedData.title);

            // Pre-populate form with AI-generated data
            setFormData({
              title: parsedData.title || '',
              project_type: parsedData.project_type || 'app',
              description: parsedData.description || '',
              concept_details: parsedData.concept_details || '',
              key_features: Array.isArray(parsedData.key_features) && parsedData.key_features.length > 0
                ? parsedData.key_features.map((f: any) => ({
                  text: typeof f === 'string' ? f : (f.text || ''),
                  screenshot: null,
                  existingScreenshotUrl: undefined
                }))
                : [{ text: '', screenshot: null, existingScreenshotUrl: undefined }],
              proposed_price: parsedData.proposed_price ? parsedData.proposed_price.toString() : '',
              early_bird_discount: parsedData.early_bird_discount ? parsedData.early_bird_discount.toString() : '',
              estimated_timeline: parsedData.estimated_timeline || '',
              project_category: parsedData.project_category || '',
              image_url: parsedData.image_url || '',
              demo_url: parsedData.demo_url || ''
            });

            // Show success message
            setUIState(prev => ({
              ...prev,
              success: t('createProject.aiProjectLoaded')
            }));

            // Clear the AI data from sessionStorage after loading
            sessionStorage.removeItem(preReleaseKey);

            // Clear success message after a few seconds
            setTimeout(() => {
              setUIState(prev => ({ ...prev, success: null }));
            }, 5000);

            return; // Don't check for draft if we loaded AI data
          } catch (error) {
            console.error('Failed to parse AI-generated data:', error);
          }
        }
      }

      // Check for draft in create mode (only if no AI data was loaded)
      const draft = loadDraft();
      if (draft && !isEditMode) {
        // Show draft recovery option
        setUIState(prev => ({
          ...prev,
          confirmDialog: {
            isOpen: true,
            type: 'unsavedChanges',
            title: t('createProject.dialogRecoverDraftTitle'),
            message: t('createProject.dialogRecoverDraftMessage'),
            details: t('createProject.dialogRecoverDraftDetails', { date: new Date(draft.timestamp).toLocaleString() }),
            confirmText: t('createProject.dialogRecoverDraftConfirm'),
            cancelText: t('createProject.dialogRecoverDraftCancel'),
            onConfirm: () => {
              // Load draft data
              const { timestamp, mode, projectId: draftProjectId, ...draftFormData } = draft;
              setFormData(draftFormData);
              setUIState(prev => ({
                ...prev,
                confirmDialog: null,
                success: 'Draft recovered successfully!'
              }));
              // Clear success message after a few seconds
              setTimeout(() => {
                setUIState(prev => ({ ...prev, success: null }));
              }, 3000);
            },
            warningLevel: 'info'
          }
        }));
      }
    }
  }, [isEditMode, projectId, loadDraft, searchParams]);

  // Detect actual changes by comparing current form data with original project
  const hasActualChanges = useCallback(() => {
    if (!isEditMode || !uiState.originalProject) {
      return false;
    }

    const original = uiState.originalProject;

    // Check basic fields
    if (formData.title.trim() !== (original.title || '')) return true;
    if (formData.description.trim() !== (original.description || '')) return true;
    if (formData.concept_details.trim() !== (original.concept_details || '')) return true;
    if (formData.project_type !== (original.project_type || 'app')) return true;
    if (formData.project_category !== (original.project_category || '')) return true;
    if (formData.estimated_timeline !== (original.estimated_timeline || '')) return true;
    if (formData.image_url.trim() !== (original.image_url || '')) return true;
    if (formData.demo_url.trim() !== (original.demo_url || '')) return true;

    // Check pricing fields
    const currentPrice = formData.proposed_price ? parseFloat(formData.proposed_price) : undefined;
    const originalPrice = original.proposed_price;
    if (currentPrice !== originalPrice) return true;

    const currentDiscount = formData.early_bird_discount ? parseFloat(formData.early_bird_discount) : undefined;
    const originalDiscount = original.early_bird_discount;
    if (currentDiscount !== originalDiscount) return true;

    // Check key features
    const currentFeatures = formData.key_features
      .filter(f => f.text.trim() !== '')
      .map(f => f.text.trim());

    const originalFeatures = Array.isArray(original.key_features)
      ? original.key_features.map(f => {
        if (typeof f === 'string') {
          try {
            const parsed = JSON.parse(f);
            return parsed && typeof parsed === 'object' ? (parsed.text || f) : f;
          } catch (e) {
            return f;
          }
        } else if (typeof f === 'object' && f !== null) {
          return f.text || '';
        }
        return String(f || '');
      }).filter(text => text.trim() !== '')
      : [];

    if (currentFeatures.length !== originalFeatures.length) return true;
    if (currentFeatures.some((feature, index) => feature !== originalFeatures[index])) return true;

    // Check for new screenshots
    if (formData.key_features.some(f => f.screenshot !== null)) return true;

    // Check for removed screenshots
    if (formData.key_features.some((f, index) => {
      const originalFeature = Array.isArray(original.key_features) && original.key_features[index];
      let hadScreenshot = false;

      if (originalFeature) {
        if (typeof originalFeature === 'string') {
          try {
            const parsed = JSON.parse(originalFeature);
            hadScreenshot = parsed && typeof parsed === 'object' && parsed.screenshot_url;
          } catch (e) {
            hadScreenshot = false;
          }
        } else if (typeof originalFeature === 'object' && originalFeature !== null) {
          hadScreenshot = !!originalFeature.screenshot_url;
        }
      }

      const hasScreenshot = f.existingScreenshotUrl || f.screenshot;
      return hadScreenshot && !hasScreenshot;
    })) return true;

    return false;
  }, [isEditMode, uiState.originalProject, formData]);

  // Handle unsaved changes warning when leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActualChanges()) {
        e.preventDefault();
        const message = t('createProject.dialogUnsavedChangesWarning');
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActualChanges]);



  // Comprehensive validation functions
  const validateField = useCallback((field: string, value: any): ValidationError | null => {
    switch (field) {
      case 'title':
        if (!value || value.trim().length === 0) {
          return { field, message: t('createProject.errorTitleRequired'), type: 'required' };
        }
        if (value.trim().length < 3) {
          return { field, message: t('createProject.errorTitleMinLength', { min: 3 }), type: 'length' };
        }
        if (value.trim().length > 100) {
          return { field, message: t('createProject.errorTitleMaxLength', { max: 100 }), type: 'length' };
        }
        break;

      case 'description':
        if (!value || value.trim().length === 0) {
          return { field, message: t('createProject.errorDescriptionRequired'), type: 'required' };
        }
        if (value.trim().length < 10) {
          return { field, message: t('createProject.errorDescriptionMinLength', { min: 10 }), type: 'length' };
        }
        if (value.trim().length > 500) {
          return { field, message: t('createProject.errorDescriptionMaxLength', { max: 500 }), type: 'length' };
        }
        break;

      case 'concept_details':
        if (value && value.trim().length > 1000) {
          return { field, message: t('createProject.errorConceptMaxLength', { max: 1000 }), type: 'length' };
        }
        break;

      case 'key_features':
        if (!Array.isArray(value) || !value.some(f => f.text && f.text.trim().length > 0)) {
          return { field, message: t('createProject.errorFeatureRequired'), type: 'required' };
        }
        // Validate individual features
        for (let i = 0; i < value.length; i++) {
          const feature = value[i];
          if (feature.text && feature.text.trim().length > 0) {
            if (feature.text.trim().length < 3) {
              return { field: `key_features[${i}]`, message: t('createProject.errorFeatureMinLength', { number: i + 1, min: 3 }), type: 'length' };
            }
            if (feature.text.trim().length > 200) {
              return { field: `key_features[${i}]`, message: t('createProject.errorFeatureMaxLength', { number: i + 1, max: 200 }), type: 'length' };
            }
          }
          // Validate screenshot files
          if (feature.screenshot) {
            const fileError = validateFile(feature.screenshot);
            if (fileError) {
              return { field: `key_features[${i}].screenshot`, message: `${t('createProject.keyFeatures')} ${i + 1} screenshot: ${fileError}`, type: 'file' };
            }
          }
        }
        break;

      case 'proposed_price':
        const priceValidation = PricingValidationService.validateProposedPrice(value);
        if (!priceValidation.isValid) {
          return {
            field,
            message: priceValidation.errors[0].message,
            type: priceValidation.errors[0].type as 'required' | 'format' | 'length' | 'file' | 'business'
          };
        }
        break;

      case 'early_bird_discount':
        const discountValidation = PricingValidationService.validateEarlyBirdDiscount(value);
        if (!discountValidation.isValid) {
          return {
            field,
            message: discountValidation.errors[0].message,
            type: discountValidation.errors[0].type as 'required' | 'format' | 'length' | 'file' | 'business'
          };
        }
        break;

      case 'image_url':
        if (value && value.trim() !== '' && !isValidUrl(value)) {
          return { field, message: t('createProject.errorImageUrlInvalid'), type: 'format' };
        }
        if (value && value.trim().length > 500) {
          return { field, message: t('createProject.errorImageUrlTooLong'), type: 'length' };
        }
        break;

      case 'demo_url':
        if (value && value.trim() !== '' && !isValidUrl(value)) {
          return { field, message: t('createProject.errorDemoUrlInvalid'), type: 'format' };
        }
        if (value && value.trim().length > 500) {
          return { field, message: t('createProject.errorDemoUrlTooLong'), type: 'length' };
        }
        break;
    }
    return null;
  }, []);

  // Validate all fields and return errors
  const validateAllFields = useCallback((): ValidationError[] => {
    const errors: ValidationError[] = [];

    // Validate basic fields
    const titleError = validateField('title', formData.title);
    if (titleError) errors.push(titleError);

    const descriptionError = validateField('description', formData.description);
    if (descriptionError) errors.push(descriptionError);

    const conceptError = validateField('concept_details', formData.concept_details);
    if (conceptError) errors.push(conceptError);

    // Validate details fields
    const featuresError = validateField('key_features', formData.key_features);
    if (featuresError) errors.push(featuresError);

    const priceError = validateField('proposed_price', formData.proposed_price);
    if (priceError) errors.push(priceError);

    const discountError = validateField('early_bird_discount', formData.early_bird_discount);
    if (discountError) errors.push(discountError);

    // Additional combined pricing validation
    if (!priceError && !discountError) {
      const combinedValidation = PricingValidationService.validatePricingCombination(
        formData.proposed_price,
        formData.early_bird_discount
      );

      // Add any additional errors from combined validation
      combinedValidation.errors.forEach(error => {
        if (!errors.some(e => e.field === error.field && e.message === error.message)) {
          errors.push({
            field: error.field,
            message: error.message,
            type: error.type as 'required' | 'format' | 'length' | 'file' | 'business'
          });
        }
      });
    }

    // Validate media fields
    const imageError = validateField('image_url', formData.image_url);
    if (imageError) errors.push(imageError);

    const demoError = validateField('demo_url', formData.demo_url);
    if (demoError) errors.push(demoError);

    return errors;
  }, [formData, validateField]);

  // Step-specific validation functions
  const validateBasicStep = useCallback((): boolean => {
    const titleError = validateField('title', formData.title);
    const descriptionError = validateField('description', formData.description);
    const conceptError = validateField('concept_details', formData.concept_details);

    return !titleError && !descriptionError && !conceptError;
  }, [formData.title, formData.description, formData.concept_details, validateField]);

  const validateDetailsStep = useCallback((): boolean => {
    const featuresError = validateField('key_features', formData.key_features);
    const priceError = validateField('proposed_price', formData.proposed_price);
    const discountError = validateField('early_bird_discount', formData.early_bird_discount);

    return !featuresError && !priceError && !discountError;
  }, [formData.key_features, formData.proposed_price, formData.early_bird_discount, validateField]);

  const validateMediaStep = useCallback((): boolean => {
    const imageError = validateField('image_url', formData.image_url);
    const demoError = validateField('demo_url', formData.demo_url);

    return !imageError && !demoError;
  }, [formData.image_url, formData.demo_url, validateField]);

  // URL validation helper
  const isValidUrl = (url: string): boolean => {
    if (!url.trim()) return true; // Empty URLs are valid (optional fields)

    try {
      const urlObj = new URL(url);
      return ['http:', 'https:'].includes(urlObj.protocol);
    } catch {
      return false;
    }
  };

  // Update validation rules based on project status and business logic
  const validateUpdatePermissions = useCallback((field: string, newValue: any): { allowed: boolean; reason?: string } => {
    if (!isEditMode || !uiState.originalProject) {
      return { allowed: true }; // No restrictions for create mode
    }

    const project = uiState.originalProject;

    switch (field) {
      case 'proposed_price':
      case 'early_bird_discount':
        // Pricing changes are restricted if users have expressed willingness to pay
        // For now, we'll allow changes but could add confirmation dialog later
        return { allowed: true };

      case 'project_type':
        // Project type can only be changed if status is 'concept'
        if (project.status !== 'concept') {
          return {
            allowed: false,
            reason: 'Project type cannot be changed after the concept phase.'
          };
        }
        return { allowed: true };

      case 'estimated_timeline':
        // Timeline can only be changed if status is 'concept' or 'development'
        if (!['concept', 'development'].includes(project.status)) {
          return {
            allowed: false,
            reason: 'Timeline cannot be changed for released or cancelled projects.'
          };
        }
        return { allowed: true };

      case 'key_features':
        // Features can be added, but removing features with feedback should be restricted
        // For now, we'll allow all changes but could add more sophisticated logic later
        return { allowed: true };

      case 'title':
      case 'description':
      case 'concept_details':
      case 'project_category':
      case 'image_url':
      case 'demo_url':
        // These fields are always updatable
        return { allowed: true };

      default:
        return { allowed: true };
    }
  }, [isEditMode, uiState.originalProject]);

  // Check if project has restrictions based on status
  const getProjectRestrictions = useCallback(() => {
    if (!isEditMode || !uiState.originalProject) {
      return { hasRestrictions: false, restrictions: [] };
    }

    const project = uiState.originalProject;
    const restrictions: string[] = [];

    if (project.status === 'released') {
      restrictions.push(t('createProject.restrictionReleased'));
    } else if (project.status === 'cancelled') {
      restrictions.push(t('createProject.restrictionCancelled'));
    } else if (project.status === 'development') {
      restrictions.push('This project is in development. Some fields may have restrictions.');
    }

    return {
      hasRestrictions: restrictions.length > 0,
      restrictions
    };
  }, [isEditMode, uiState.originalProject]);

  // Check if pricing changes need confirmation
  const needsPriceChangeConfirmation = useCallback(() => {
    if (!isEditMode || !uiState.originalProject) {
      return false;
    }

    const original = uiState.originalProject;
    const currentPrice = formData.proposed_price ? parseFloat(formData.proposed_price) : undefined;
    const originalPrice = original.proposed_price;
    const currentDiscount = formData.early_bird_discount ? parseFloat(formData.early_bird_discount) : undefined;
    const originalDiscount = original.early_bird_discount;

    // Check if pricing has changed
    const priceChanged = currentPrice !== originalPrice;
    const discountChanged = currentDiscount !== originalDiscount;

    // For now, we'll show confirmation for any price change
    // In a real implementation, you might check if users have expressed willingness to pay
    return priceChanged || discountChanged;
  }, [isEditMode, uiState.originalProject, formData.proposed_price, formData.early_bird_discount]);

  // Show confirmation dialog
  const showConfirmDialog = useCallback((
    type: 'priceChange' | 'unsavedChanges' | 'sensitiveChange',
    title: string,
    message: string,
    onConfirm: () => void,
    options?: {
      details?: string;
      confirmText?: string;
      cancelText?: string;
      warningLevel?: 'info' | 'warning' | 'danger';
    }
  ) => {
    setUIState(prev => ({
      ...prev,
      confirmDialog: {
        isOpen: true,
        type,
        title,
        message,
        details: options?.details,
        confirmText: options?.confirmText || 'Confirm',
        cancelText: options?.cancelText || 'Cancel',
        onConfirm,
        warningLevel: options?.warningLevel || 'warning'
      }
    }));
  }, []);

  // Hide confirmation dialog
  const hideConfirmDialog = useCallback(() => {
    // If this was a draft recovery dialog and user cancelled, clear the draft
    if (uiState.confirmDialog?.type === 'unsavedChanges' &&
      uiState.confirmDialog?.title === t('createProject.dialogRecoverDraftTitle')) {
      clearDraft();
    }

    setUIState(prev => ({
      ...prev,
      confirmDialog: null
    }));
  }, [uiState.confirmDialog, clearDraft]);

  // Handle unsaved changes warning
  const handleUnsavedChangesWarning = useCallback((onProceed: () => void) => {
    if (!hasActualChanges()) {
      onProceed();
      return;
    }

    showConfirmDialog(
      'unsavedChanges',
      t('createProject.dialogUnsavedChangesTitle'),
      t('createProject.dialogUnsavedChangesMessage'),
      () => {
        hideConfirmDialog();
        onProceed();
      },
      {
        details: t('createProject.dialogUnsavedChangesDetails'),
        confirmText: t('createProject.dialogUnsavedChangesConfirm'),
        cancelText: t('createProject.dialogUnsavedChangesCancel'),
        warningLevel: 'warning'
      }
    );
  }, [hasActualChanges, showConfirmDialog, hideConfirmDialog]);

  // Real-time validation and error handling
  useEffect(() => {
    const isBasicComplete = validateBasicStep();
    const isDetailsComplete = validateDetailsStep();
    const isMediaComplete = validateMediaStep();

    // Get all validation errors
    const allErrors = validateAllFields();

    // Create field-specific error map
    const fieldErrors: Record<string, string> = {};
    allErrors.forEach(error => {
      fieldErrors[error.field] = error.message;
    });

    setUIState(prev => ({
      ...prev,
      isBasicComplete,
      isDetailsComplete,
      isMediaComplete,
      validationErrors: allErrors,
      fieldErrors
    }));
  }, [formData, validateBasicStep, validateDetailsStep, validateMediaStep, validateAllFields]);

  // Get validation errors for specific steps
  const getBasicStepErrors = useCallback(() => {
    return uiState.validationErrors.filter(error =>
      ['title', 'description', 'concept_details'].includes(error.field)
    );
  }, [uiState.validationErrors]);

  const getDetailsStepErrors = useCallback(() => {
    return uiState.validationErrors.filter(error =>
      ['key_features', 'proposed_price', 'early_bird_discount'].includes(error.field) ||
      error.field.startsWith('key_features[')
    );
  }, [uiState.validationErrors]);

  const getMediaStepErrors = useCallback(() => {
    return uiState.validationErrors.filter(error =>
      ['image_url', 'demo_url'].includes(error.field)
    );
  }, [uiState.validationErrors]);

  // Update current step based on completion status
  useEffect(() => {
    let newStep = 1;

    if (uiState.isBasicComplete) {
      newStep = Math.max(newStep, 2);
    }
    if (uiState.isDetailsComplete) {
      newStep = Math.max(newStep, 3);
    }
    if (uiState.isMediaComplete) {
      newStep = Math.max(newStep, 4);
    }

    // Only update if the step has actually changed
    if (newStep !== uiState.currentStep) {
      setUIState(prev => ({ ...prev, currentStep: newStep }));
    }
  }, [uiState.isBasicComplete, uiState.isDetailsComplete, uiState.isMediaComplete, uiState.currentStep]);

  const loadProjectForEdit = async (id: string) => {
    try {
      setUIState(prev => ({ ...prev, loading: true, error: null }));

      const project = await preReleaseService.getProjectById(id);

      if (!project) {
        setUIState(prev => ({
          ...prev,
          error: t('createProject.errorProjectNotFound')
        }));
        return;
      }

      // Check if user is the owner
      if (project.publisher_id !== user?.id) {
        setUIState(prev => ({
          ...prev,
          error: t('createProject.errorNoPermission')
        }));
        return;
      }

      // Pre-populate form with project data
      const keyFeatures = Array.isArray(project.key_features)
        ? project.key_features.map(feature => {
          let featureText = '';
          let screenshotUrl = undefined;

          if (typeof feature === 'string') {
            // Try to parse as JSON first
            try {
              const parsed = JSON.parse(feature);
              if (parsed && typeof parsed === 'object') {
                featureText = parsed.text || feature;
                screenshotUrl = parsed.screenshot_url;
              } else {
                featureText = feature;
              }
            } catch (e) {
              // Not JSON, treat as plain string
              featureText = feature;
            }
          } else if (typeof feature === 'object' && feature !== null) {
            featureText = feature.text || '';
            screenshotUrl = feature.screenshot_url;
          } else {
            featureText = String(feature || '');
          }

          return {
            text: featureText,
            screenshot: null, // Screenshots are loaded as URLs, not File objects
            existingScreenshotUrl: screenshotUrl
          };
        })
        : [{ text: '', screenshot: null, existingScreenshotUrl: undefined }];

      // Ensure at least one feature exists
      if (keyFeatures.length === 0) {
        keyFeatures.push({ text: '', screenshot: null, existingScreenshotUrl: undefined });
      }

      setFormData({
        title: project.title || '',
        project_type: project.project_type || 'app',
        description: project.description || '',
        concept_details: project.concept_details || '',
        key_features: keyFeatures,
        proposed_price: project.proposed_price ? project.proposed_price.toString() : '',
        early_bird_discount: project.early_bird_discount ? project.early_bird_discount.toString() : '',
        estimated_timeline: project.estimated_timeline || '',
        project_category: project.project_category || '',
        image_url: project.image_url || '',
        demo_url: project.demo_url || ''
      });

      setUIState(prev => ({
        ...prev,
        originalProject: project,
        hasUnsavedChanges: false
      }));

      // Initialize pricing calculation for existing project
      const proposedPrice = project.proposed_price ? project.proposed_price.toString() : '';
      const earlyBirdDiscount = project.early_bird_discount ? project.early_bird_discount.toString() : '';
      updatePricingCalculation(proposedPrice, earlyBirdDiscount);

    } catch (error) {
      console.error('Error loading project:', error);
      setUIState(prev => ({
        ...prev,
        error: t('createProject.errorFailedToLoad')
      }));
    } finally {
      setUIState(prev => ({ ...prev, loading: false }));
    }
  };

  // Update pricing calculation when pricing fields change
  const updatePricingCalculation = useCallback((proposedPrice: string, earlyBirdDiscount: string) => {
    const combinedValidation = PricingValidationService.validatePricingCombination(
      proposedPrice,
      earlyBirdDiscount
    );

    setPricingCalculation(combinedValidation.calculation);
  }, []);

  // Enhanced input change handler with real-time validation
  const handleInputChangeWithValidation = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Update form data
    setFormData(prev => {
      const newFormData = { ...prev, [name]: value };

      // Update pricing calculation if pricing fields changed
      if (name === 'proposed_price' || name === 'early_bird_discount') {
        const proposedPrice = name === 'proposed_price' ? value : prev.proposed_price;
        const earlyBirdDiscount = name === 'early_bird_discount' ? value : prev.early_bird_discount;
        updatePricingCalculation(proposedPrice, earlyBirdDiscount);
      }

      return newFormData;
    });

    // Perform real-time validation for immediate feedback
    const fieldError = validateField(name, value);

    // Clear field-specific errors when user starts typing, but show new errors immediately
    setUIState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
      error: null,
      success: null,
      fieldErrors: {
        ...prev.fieldErrors,
        [name]: fieldError ? fieldError.message : '' // Show immediate validation feedback
      }
    }));
  }, [validateField, updatePricingCalculation]);

  const handleInputChange = handleInputChangeWithValidation;

  // Key features management
  const addKeyFeature = () => {
    setFormData(prev => ({
      ...prev,
      key_features: [...prev.key_features, { text: '', screenshot: null, existingScreenshotUrl: undefined }]
    }));
    setUIState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
      error: null,
      success: null
    }));
  };

  const removeKeyFeature = (index: number) => {
    if (formData.key_features.length > 1) {
      setFormData(prev => ({
        ...prev,
        key_features: prev.key_features.filter((_, i) => i !== index)
      }));
      setUIState(prev => ({
        ...prev,
        hasUnsavedChanges: true,
        error: null,
        success: null
      }));
    }
  };

  const updateKeyFeature = (index: number, text: string) => {
    setFormData(prev => ({
      ...prev,
      key_features: prev.key_features.map((feature, i) =>
        i === index ? { ...feature, text } : feature
      )
    }));
    setUIState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
      error: null,
      success: null
    }));
  };

  const updateKeyFeatureScreenshot = (index: number, file: File | null) => {
    setFormData(prev => ({
      ...prev,
      key_features: prev.key_features.map((feature, i) =>
        i === index ? { ...feature, screenshot: file } : feature
      )
    }));
    setUIState(prev => ({
      ...prev,
      hasUnsavedChanges: true,
      error: null,
      success: null
    }));
  };

  // File validation helper
  const validateFile = (file: File): string | null => {
    const maxSize = 1024 * 1024; // 1MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (file.size > maxSize) {
      return t('createProject.errorFileSizeTooLarge');
    }

    if (!allowedTypes.includes(file.type)) {
      return 'File must be an image (JPEG, PNG, WebP, or GIF)';
    }

    return null;
  };

  // Get field validation status for styling
  const getFieldValidationStatus = useCallback((fieldName: string, value: any) => {
    const error = validateField(fieldName, value);
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return 'neutral'; // No styling for empty fields
    }
    return error ? 'error' : 'success';
  }, [validateField]);

  // Get field error message
  const getFieldError = useCallback((fieldName: string) => {
    return uiState.fieldErrors[fieldName] || '';
  }, [uiState.fieldErrors]);

  // Show validation summary
  const showValidationSummary = useCallback(() => {
    setUIState(prev => ({ ...prev, showValidationSummary: true }));
  }, []);

  // Hide validation summary
  const hideValidationSummary = useCallback(() => {
    setUIState(prev => ({ ...prev, showValidationSummary: false }));
  }, []);

  // Enhanced error recovery - focus on first error field
  const focusFirstErrorField = useCallback(() => {
    const firstError = uiState.validationErrors[0];
    if (firstError) {
      const fieldElement = document.getElementById(firstError.field.replace('[', '_').replace(']', ''));
      if (fieldElement) {
        fieldElement.focus();
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [uiState.validationErrors]);

  // Auto-save draft functionality
  const saveDraft = useCallback(() => {
    if (hasActualChanges()) {
      try {
        const draftData = {
          ...formData,
          timestamp: Date.now(),
          mode: isEditMode ? 'edit' : 'create',
          projectId: projectId || null
        };
        localStorage.setItem('prerelease-project-draft', JSON.stringify(draftData));
      } catch (error) {
        console.warn('Failed to save draft:', error);
      }
    }
  }, [formData, hasActualChanges, isEditMode, projectId]);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    const interval = setInterval(saveDraft, 30000);
    return () => clearInterval(interval);
  }, [saveDraft]);

  // Load template data if coming from journey workflow
  useEffect(() => {
    const loadTemplate = async () => {
      const templateId = searchParams.get('template');
      const stepId = searchParams.get('stepId');
      const journeyId = searchParams.get('journeyId');
      const aiEnhanced = searchParams.get('aiEnhanced') === 'true';

      if (stepId && journeyId && !isEditMode) {
        try {
          // Check if this is an AI-enhanced pre-release project
          if (aiEnhanced) {
            console.log('🤖 AI-Enhanced pre-release project detected');

            // Set AI generating state
            setUIState(prev => ({ ...prev, aiGenerating: true }));

            // Extract idea information from URL parameters
            const feasibility = searchParams.get('ideaFeasibility') || 'medium';
            const selectedIdea = {
              title: searchParams.get('ideaTitle') || '',
              problem: searchParams.get('ideaProblem') || '',
              solution: searchParams.get('ideaSolution') || '',
              targetCustomer: searchParams.get('ideaTarget') || '',
              targetAudience: searchParams.get('ideaTarget') || '',
              technicalFeasibility: (feasibility === 'high' || feasibility === 'medium' || feasibility === 'low')
                ? feasibility as 'high' | 'medium' | 'low'
                : 'medium' as const,
              skillsRequired: searchParams.get('ideaSkills')?.split(',').filter(s => s.trim()) || []
            };

            // Get journey context for market and persona
            const { supabase } = await import('../../services/supabaseClient');
            const { data: journey } = await supabase
              .from('journeys')
              .select('market, persona')
              .eq('id', journeyId)
              .single();

            if (selectedIdea.title && journey) {
              console.log('🚀 Generating AI-enhanced pre-release project...');

              // Use the AI service to generate the pre-release project with user's language
              const { AIPreReleaseProjectService } = await import('../../services/aiPreReleaseProjectService');
              console.log('🌐 Generating pre-release project in language:', uiLanguage);
              
              const aiProject = await AIPreReleaseProjectService.generatePreReleaseProject(
                selectedIdea,
                journey.market,
                journey.persona,
                uiLanguage
              );

              // Pre-fill form with AI-generated data
              setFormData(prevData => {
                // Convert AI key_features to form format
                let formKeyFeatures = prevData.key_features;
                if (aiProject.key_features && Array.isArray(aiProject.key_features)) {
                  formKeyFeatures = aiProject.key_features.map((feature: any) => ({
                    text: typeof feature === 'string' ? feature : feature.text || '',
                    screenshot: null,
                    existingScreenshotUrl: typeof feature === 'object' ? feature.screenshot_url : undefined
                  }));
                }

                return {
                  ...prevData,
                  title: aiProject.title || prevData.title,
                  project_type: aiProject.project_type || prevData.project_type,
                  description: aiProject.description || prevData.description,
                  concept_details: aiProject.concept_details || prevData.concept_details,
                  key_features: formKeyFeatures,
                  proposed_price: aiProject.proposed_price?.toString() || prevData.proposed_price,
                  early_bird_discount: aiProject.early_bird_discount?.toString() || prevData.early_bird_discount,
                  estimated_timeline: aiProject.estimated_timeline || prevData.estimated_timeline,
                  project_category: aiProject.project_category || prevData.project_category
                };
              });

              console.log('✅ Pre-filled form with AI-generated content for:', selectedIdea.title);

              // Clear AI generating state
              setUIState(prev => ({ ...prev, aiGenerating: false }));
              return; // Skip regular template loading
            }

            // Clear AI generating state if we didn't generate
            setUIState(prev => ({ ...prev, aiGenerating: false }));
          }

          // Regular template loading (fallback or non-AI enhanced)
          const integrationData = await JourneyPlatformIntegrationService.prepareIntegrationData(
            stepId,
            'preRelease',
            templateId || undefined, // Allow undefined for contextual matching
            journeyId
          );

          // Extract template data if we have a template
          let templateData = integrationData.template
            ? JourneyPlatformIntegrationService.extractTemplateData(integrationData.template)
            : {};

          // Enrich the template data with journey context
          const enrichedData = await JourneyPlatformIntegrationService.enrichPreReleaseData(
            journeyId,
            templateData
          );

          // Pre-fill form with enriched template data
          setFormData(prevData => ({
            ...prevData,
            title: enrichedData.title || prevData.title,
            project_type: enrichedData.project_type || prevData.project_type,
            description: enrichedData.description || prevData.description,
            concept_details: enrichedData.concept_details || prevData.concept_details,
            key_features: enrichedData.key_features || prevData.key_features,
            proposed_price: enrichedData.proposed_price || prevData.proposed_price,
            early_bird_discount: enrichedData.early_bird_discount || prevData.early_bird_discount,
            estimated_timeline: enrichedData.estimated_timeline || prevData.estimated_timeline,
            project_category: enrichedData.project_category || prevData.project_category,
            image_url: enrichedData.image_url || prevData.image_url,
            demo_url: enrichedData.demo_url || prevData.demo_url
          }));

          console.log('✅ Pre-filled form with enriched journey context data');
        } catch (err) {
          console.error('Failed to load template or generate AI content:', err);
          // Clear AI generating state on error
          setUIState(prev => ({ ...prev, aiGenerating: false }));
          // Don't show error to user, just log it and continue with regular form
        }
      }
    };

    loadTemplate();
  }, [searchParams, isEditMode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !uiState.isBasicComplete || !uiState.isDetailsComplete || !uiState.isMediaComplete) {
      return;
    }

    // Check if price change confirmation is needed
    if (isEditMode && needsPriceChangeConfirmation()) {
      showConfirmDialog(
        'priceChange',
        'Confirm Price Changes',
        'You have made changes to the pricing information. This may affect users who have already expressed interest in your project.',
        () => {
          hideConfirmDialog();
          performSubmit();
        },
        {
          details: t('createProject.dialogPricingChangeDetails'),
          confirmText: t('createProject.dialogPricingChangeConfirm'),
          cancelText: t('createProject.dialogPricingChangeCancel'),
          warningLevel: 'warning'
        }
      );
      return;
    }

    // Proceed with normal submission
    performSubmit();
  };

  const performSubmit = async () => {
    try {
      // Validate all fields before submission
      const allErrors = validateAllFields();
      if (allErrors.length > 0) {
        setUIState(prev => ({
          ...prev,
          error: allErrors.length > 1 
            ? t('createProject.validationErrorCount', { count: allErrors.length })
            : t('createProject.validationErrorCountSingular', { count: allErrors.length }),
          showValidationSummary: true
        }));

        // Focus on first error field for better UX
        setTimeout(() => {
          focusFirstErrorField();
        }, 100);

        return;
      }

      setUIState(prev => ({
        ...prev,
        loading: true,
        submitting: true,
        error: null,
        success: null,
        showValidationSummary: false
      }));

      // Transform form data to match the service interface
      const projectData: Omit<PreReleaseProject, 'id' | 'created_at' | 'updated_at' | 'publisher' | 'publisher_id'> = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        concept_details: formData.concept_details.trim() || undefined,
        project_type: formData.project_type,
        key_features: formData.key_features
          .filter(feature => feature.text.trim() !== '')
          .map(feature => feature.text.trim()),
        proposed_price: formData.proposed_price ? parseFloat(formData.proposed_price) : undefined,
        early_bird_discount: formData.early_bird_discount ? parseFloat(formData.early_bird_discount) : undefined,
        estimated_timeline: formData.estimated_timeline || undefined,
        project_category: formData.project_category || undefined,
        image_url: formData.image_url.trim() || undefined,
        demo_url: formData.demo_url.trim() || undefined,
        status: 'concept',
        is_active: true
      };

      // Extract screenshot files for upload
      const featureScreenshots = formData.key_features
        .filter(feature => feature.text.trim() !== '')
        .map(feature => feature.screenshot);

      console.log('Submitting project data:', projectData);
      console.log('Feature screenshots:', featureScreenshots.map((file, index) => ({
        index,
        hasFile: !!file,
        fileName: file?.name,
        fileSize: file?.size
      })));

      let resultProject: PreReleaseProject;

      if (isEditMode && projectId) {
        // Update existing project
        console.log('Updating project:', projectId);

        // Check for validation restrictions
        const restrictions = getProjectRestrictions();
        if (restrictions.hasRestrictions && uiState.originalProject?.status === 'cancelled') {
          throw new Error('This project has been cancelled and cannot be edited.');
        }

        // For edit mode, handle screenshots more carefully
        const hasNewScreenshots = featureScreenshots.some(file => file !== null);

        if (hasNewScreenshots) {
          // If there are new screenshots, we need to create enhanced key features
          // by combining existing screenshot URLs with new uploads
          const enhancedKeyFeatures: Array<{ text: string; screenshot_url?: string }> = [];

          for (let i = 0; i < formData.key_features.length; i++) {
            const feature = formData.key_features[i];
            if (feature.text.trim() === '') continue; // Skip empty features

            const enhancedFeature: { text: string; screenshot_url?: string } = {
              text: feature.text.trim()
            };

            // If there's a new screenshot file, upload it
            if (feature.screenshot && feature.screenshot instanceof File) {
              try {
                // Set uploading state for this specific file
                setUIState(prev => ({ ...prev, uploadingFiles: true }));

                const uploadedUrl = await preReleaseService.uploadScreenshot(feature.screenshot, projectId, i);
                if (uploadedUrl && uploadedUrl.trim()) {
                  enhancedFeature.screenshot_url = uploadedUrl;
                }
              } catch (uploadError) {
                console.warn(`Failed to upload screenshot for feature ${i}:`, uploadError);

                // Show specific error for file upload failures
                const errorMsg = uploadError instanceof Error ? uploadError.message : t('createProject.errorUnknownUpload');
                setUIState(prev => ({
                  ...prev,
                  error: t('createProject.errorFailedUploadScreenshot', { number: i + 1, error: errorMsg }),
                  uploadingFiles: false,
                  submitting: false,
                  loading: false
                }));
                return; // Stop submission on upload failure
              } finally {
                setUIState(prev => ({ ...prev, uploadingFiles: false }));
              }
            } else if (feature.existingScreenshotUrl) {
              // Keep existing screenshot URL
              enhancedFeature.screenshot_url = feature.existingScreenshotUrl;
            }

            enhancedKeyFeatures.push(enhancedFeature);
          }

          const updateData = {
            ...projectData,
            key_features: enhancedKeyFeatures
          };

          resultProject = await preReleaseService.updateProject(projectId, updateData);
        } else {
          // No new screenshots, just update with existing data
          // Preserve existing screenshot URLs
          const enhancedKeyFeatures = formData.key_features
            .filter(feature => feature.text.trim() !== '')
            .map(feature => ({
              text: feature.text.trim(),
              ...(feature.existingScreenshotUrl && { screenshot_url: feature.existingScreenshotUrl })
            }));

          const updateData = {
            ...projectData,
            key_features: enhancedKeyFeatures
          };

          resultProject = await preReleaseService.updateProject(projectId, updateData);
        }

        console.log('Project updated successfully:', resultProject);
      } else {
        // Create new project with screenshot uploads
        resultProject = await preReleaseService.createProject(projectData, featureScreenshots);
        console.log('Project created successfully:', resultProject);
      }

      // Clear draft on successful submission
      clearDraft();

      // Track completion if this came from a journey workflow
      const stepId = searchParams.get('stepId');
      const journeyId = searchParams.get('journeyId');
      if (stepId && resultProject.id && !isEditMode) {
        try {
          await JourneyPlatformIntegrationService.trackActionCompletion(
            stepId,
            'preRelease',
            resultProject.id
          );

          // Save pre-release project info to sessionStorage for journey display
          if (journeyId) {
            const preReleaseProjectInfo = {
              projectId: resultProject.id,
              projectTitle: resultProject.title,
              createdAt: new Date().toISOString()
            };
            const preReleaseTrackingKey = `pre_release_project_${journeyId}`;
            sessionStorage.setItem(preReleaseTrackingKey, JSON.stringify(preReleaseProjectInfo));
            console.log('✅ Saved pre-release project info to sessionStorage:', preReleaseProjectInfo);
          }
        } catch (trackingError) {
          console.error('Failed to track action completion:', trackingError);
          // Don't fail the whole operation if tracking fails
        }
      }

      // Show success message briefly before navigation
      const successMessage = isEditMode
        ? 'Project updated successfully!'
        : 'Project created successfully!';

      setUIState(prev => ({
        ...prev,
        success: successMessage,
        hasUnsavedChanges: false
      }));

      // Navigate after a brief delay to show success message
      setTimeout(() => {
        if (resultProject.id) {
          navigate(`/pre-release/${resultProject.id}`);
        } else {
          navigate('/pre-release');
        }
      }, 1500);

    } catch (error) {
      console.error('Error submitting form:', error);

      let errorMessage = isEditMode
        ? t('createProject.errorFailedToUpdate')
        : t('createProject.errorFailedToCreate');

      let errorType: 'network' | 'validation' | 'permission' | 'storage' | 'unknown' = 'unknown';
      let recoveryActions: string[] = [];

      if (error instanceof Error) {
        // Handle specific error types with recovery suggestions
        if (error.message.includes('User must be authenticated')) {
          errorMessage = t('createProject.errorMustLogin');
          errorType = 'permission';
          recoveryActions = [t('createProject.errorLoginAgain'), t('createProject.errorRefreshPage')];
        } else if (error.message.includes('Storage permissions') || error.message.includes('upload')) {
          const action = isEditMode 
            ? t('createProject.errorStorageActionUpdated')
            : t('createProject.errorStorageActionCreated');
          errorMessage = t('createProject.errorStoragePermissions', { action });
          errorType = 'storage';
          recoveryActions = [
            t('createProject.errorTryUploadSmaller'), 
            t('createProject.errorCheckInternet'), 
            t('createProject.errorAddScreenshotsLater')
          ];
        } else if (error.message.includes('duplicate key') || error.message.includes('already exists')) {
          errorMessage = t('createProject.errorDuplicateTitle');
          errorType = 'validation';
          recoveryActions = [t('createProject.errorChangeTitle'), t('createProject.errorMakeTitleSpecific')];
          // Focus on title field
          setTimeout(() => {
            const titleField = document.getElementById('title');
            if (titleField) {
              titleField.focus();
              titleField.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
        } else if (error.message.includes('violates check constraint')) {
          errorMessage = t('createProject.errorCheckConstraint');
          errorType = 'validation';
          recoveryActions = [
            t('createProject.errorReviewFields'), 
            t('createProject.errorEnsureRequired'), 
            t('createProject.errorCheckFormat')
          ];
          setTimeout(() => {
            focusFirstErrorField();
          }, 100);
        } else if (error.message.includes('not found') || error.message.includes('permission')) {
          errorMessage = t('createProject.errorProjectNotFound');
          errorType = 'permission';
          recoveryActions = [
            t('createProject.errorVerifyOwner'), 
            t('createProject.errorCheckProjectExists'), 
            t('createProject.errorTryRefreshing')
          ];
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = t('createProject.errorNetworkOccurred');
          errorType = 'network';
          recoveryActions = [
            t('createProject.errorCheckInternet'), 
            t('createProject.errorTryAgainMoments'), 
            t('createProject.errorDraftSaved')
          ];
        } else {
          errorMessage = error.message;
          recoveryActions = [
            t('createProject.errorTryAgainMoments'), 
            t('createProject.errorCheckInternet'), 
            t('createProject.errorContactSupport')
          ];
        }
      }

      // Save draft on error (except for validation errors)
      if (errorType !== 'validation') {
        saveDraft();
        if (!recoveryActions.includes('Your draft has been saved automatically')) {
          recoveryActions.push('Your draft has been saved automatically');
        }
      }

      setUIState(prev => ({
        ...prev,
        error: errorMessage,
        errorType,
        recoveryActions
      }));
    } finally {
      setUIState(prev => ({
        ...prev,
        loading: false,
        submitting: false,
        uploadingFiles: false
      }));
    }
  };

  const tabNavigation = (tab: 'basic' | 'details' | 'media' | 'review') => {
    // In create mode, validate navigation permissions
    if (!isEditMode) {
      if (tab === 'details' && !uiState.isBasicComplete) {
        // Show validation errors for basic step
        setUIState(prev => ({
          ...prev,
          error: t('createProject.errorCompleteBasicFirst')
        }));
        return;
      }

      if (tab === 'media' && (!uiState.isBasicComplete || !uiState.isDetailsComplete)) {
        setUIState(prev => ({
          ...prev,
          error: t('createProject.errorCompletePreviousSteps')
        }));
        return;
      }

      if (tab === 'review' && (!uiState.isBasicComplete || !uiState.isDetailsComplete)) {
        setUIState(prev => ({
          ...prev,
          error: t('createProject.errorCompleteAllSteps')
        }));
        return;
      }
    }

    // In edit mode, allow free navigation between tabs without unsaved changes warning
    // The warning should only appear when trying to leave the page entirely

    // Clear any navigation errors and success messages
    setUIState(prev => ({ ...prev, activeTab: tab, error: null, success: null }));
  };

  const pageTitle = isEditMode ? `${t('createProject.editPageTitle')} ${uiState.originalProject?.title || ''}` : t('createProject.pageTitle');
  const actualChanges = hasActualChanges();
  const submitButtonText = isEditMode
    ? (actualChanges ? t('createProject.saveChanges') : t('createProject.noChanges'))
    : t('createProject.createProjectButton');

  return (
    <BasePageLayout background="white" padding="none" maxWidth="xl">
      <div className="create-prerelease-project-page">
        {/* Journey Back Button */}
        <JourneyBackButton variant="floating" />

        {/* Form Container */}
        <div className="form-container">
          <div className="create-project-card">
            {/* Page Header */}
            <div className="page-header">
              <div className="page-title-wrapper">
                <h1 className="page-title">{pageTitle}</h1>
                <h5 className="page-subtitle">{isEditMode
                  ? t('createProject.editPageSubtitle')
                  : t('createProject.pageSubtitle')
                }</h5>
              </div>
            </div>

            {/* Eligibility Loading State */}
            {!isEditMode && eligibilityLoading && (
              <div className="eligibility-loading">
                <div className="d-flex align-items-center justify-content-center p-4">
                  <div className="spinner-border text-primary me-3" role="status">
                    <span className="visually-hidden">{t('createProject.loading')}</span>
                  </div>
                  <div>
                    <h5 className="mb-1">{t('createProject.checkingRequirements')}</h5>
                    <p className="text-muted mb-0">{t('createProject.verifyingEligibility')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Project Creation Requirements Check */}
            {!isEditMode && !eligibilityLoading && !canCreate && (
              <ProjectCreationRequirements
                eligibility={eligibility!}
                error={eligibilityError || undefined}
                onRetry={refetchEligibility}
                showRetryButton={true}
              />
            )}

            {/* Edit Mode Restrictions Warning */}
            {isEditMode && (() => {
              const restrictions = getProjectRestrictions();
              return restrictions.hasRestrictions && (
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle"></i>
                  <div>
                    <strong>{t('createProject.editRestrictions')}</strong>
                    <ul className="mb-0 mt-1">
                      {restrictions.restrictions.map((restriction, index) => (
                        <li key={index}>{restriction}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })()}

            {/* Show form content only if user is eligible or in edit mode */}
            {(isEditMode || canCreate || eligibilityLoading) && (
              <>
                {/* Progress Indicator */}
                <div className="step-progress">
                  <div
                    className={`step-item ${uiState.currentStep >= 1 ? 'active' : ''} ${uiState.isBasicComplete ? 'completed' : ''}`}
                    onClick={() => tabNavigation('basic')}
                  >
                    <div className="step-number">1</div>
                    <div className="step-label">{t('createProject.step1')}</div>
                  </div>
                  <div className="step-connector"></div>
                  <div
                    className={`step-item ${uiState.currentStep >= 2 ? 'active' : ''} ${uiState.isDetailsComplete ? 'completed' : ''}`}
                    onClick={() => tabNavigation('details')}
                  >
                    <div className="step-number">2</div>
                    <div className="step-label">{t('createProject.step2')}</div>
                  </div>
                  <div className="step-connector"></div>
                  <div
                    className={`step-item ${uiState.currentStep >= 3 ? 'active' : ''} ${uiState.isMediaComplete ? 'completed' : ''}`}
                    onClick={() => tabNavigation('media')}
                  >
                    <div className="step-number">3</div>
                    <div className="step-label">{t('createProject.step3')}</div>
                  </div>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="form-content">
                  <div className="tab-content">
                    {uiState.error && (
                      <div className={`alert alert-danger ${uiState.errorType ? `alert-${uiState.errorType}` : ''}`}>
                        <div className="alert-content">
                          <div className="alert-header">
                            <i className="bi bi-exclamation-triangle"></i>
                            <span className="alert-message">{uiState.error}</span>
                          </div>
                          {uiState.recoveryActions && uiState.recoveryActions.length > 0 && (
                            <div className="alert-recovery">
                              <strong>{t('createProject.whatYouCanDo')}</strong>
                              <ul className="recovery-actions">
                                {uiState.recoveryActions.map((action, index) => (
                                  <li key={index}>{action}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                        {uiState.errorType === 'network' && (
                          <button
                            type="button"
                            className="btn-retry"
                            onClick={() => {
                              setUIState(prev => ({ ...prev, error: null, errorType: undefined, recoveryActions: undefined }));
                              // Retry the last action
                              if (uiState.submitting) {
                                handleSubmit(new Event('submit') as any);
                              }
                            }}
                          >
                            <i className="bi bi-arrow-clockwise"></i>
                            {t('createProject.buttonTryAgain')}
                          </button>
                        )}
                      </div>
                    )}

                    {uiState.success && (
                      <div className="alert alert-success">
                        <div className="alert-header">
                          <i className="bi bi-check-circle"></i>
                          <span className="alert-message">{uiState.success}</span>
                        </div>
                      </div>
                    )}

                    {/* Loading indicator */}
                    {/* {uiState.loading && !uiState.submitting && (
                   <div className="loading-container">
                    <div className="loading-ring">
                    <p>{isEditMode ? 'Loading project data...' : 'Initializing form...'}</p>
                    </div>
                   </div>
                )} */}

                    {/* Step 1: Basic Information */}
                    <div className={`tab-pane ${uiState.activeTab === 'basic' ? 'active' : ''}`}>
                      <div className="form-section">
                        <h2 className="section-title">
                          <i className="bi bi-info-circle section-icon"></i>
                          {t('createProject.sectionBasicInfo')}
                        </h2>





                        <div className="form-grid form-grid-2col">
                          <div className="form-group">
                            <label htmlFor="title" className="form-label">
                              {t('createProject.titleRequired')}
                            </label>
                            <input
                              type="text"
                              id="title"
                              name="title"
                              className={`form-control ${getFieldValidationStatus('title', formData.title) === 'error'
                                ? 'form-control-error'
                                : getFieldValidationStatus('title', formData.title) === 'success'
                                  ? 'form-control-success'
                                  : ''
                                }`}
                              value={formData.title}
                              onChange={handleInputChange}
                              disabled={uiState.loading || uiState.submitting}
                              required
                              placeholder={t('createProject.titlePlaceholder')}
                              minLength={3}
                              maxLength={100}
                              aria-describedby={getFieldError('title') ? 'title-error' : undefined}
                            />
                            {getFieldError('title') && (
                              <div id="title-error" className="field-error" role="alert">
                                <i className="bi bi-exclamation-circle"></i>
                                {getFieldError('title')}
                              </div>
                            )}
                            <div className="form-help">
                              {formData.title.length > 0 ? (
                                <span className={getFieldValidationStatus('title', formData.title) === 'success' ? 'text-success' : 'text-error'}>
                                  {formData.title.trim().length}/100 {t('createProject.characters')} ({t('createProject.errorMinLength', { min: 3 })})
                                </span>
                              ) : (
                                <span>{t('createProject.titleHelp')}</span>
                              )}
                            </div>
                          </div>

                          <div className="form-group">
                            <label htmlFor="project_type" className="form-label">
                              {t('createProject.projectTypeRequired')}
                            </label>
                            <select
                              id="project_type"
                              name="project_type"
                              className="form-select form-control-success"
                              value={formData.project_type}
                              onChange={handleInputChange}
                              disabled={uiState.loading}
                              required
                            >
                              <option value="app">{t('createProject.typeApp')}</option>
                              <option value="website">{t('createProject.typeWebsite')}</option>
                              <option value="product">{t('createProject.typeProduct')}</option>
                              <option value="service">{t('createProject.typeService')}</option>
                              <option value="other">{t('createProject.typeOther')}</option>
                            </select>
                            <div className="form-help">
                              {t('createProject.projectCategoryHelp')}
                            </div>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="description" className="form-label">
                            {t('createProject.descriptionRequired')}
                          </label>
                          <textarea
                            id="description"
                            name="description"
                            className={`form-control ${getFieldValidationStatus('description', formData.description) === 'error'
                              ? 'form-control-error'
                              : getFieldValidationStatus('description', formData.description) === 'success'
                                ? 'form-control-success'
                                : ''
                              }`}
                            rows={4}
                            value={formData.description}
                            onChange={handleInputChange}
                            disabled={uiState.loading || uiState.submitting}
                            required
                            placeholder={t('createProject.descriptionPlaceholder')}
                            minLength={10}
                            maxLength={500}
                            aria-describedby={getFieldError('description') ? 'description-error' : undefined}
                          />
                          {getFieldError('description') && (
                            <div id="description-error" className="field-error" role="alert">
                              <i className="bi bi-exclamation-circle"></i>
                              {getFieldError('description')}
                            </div>
                          )}
                          <div className="form-help">
                            {formData.description.length > 0 ? (
                              <span className={getFieldValidationStatus('description', formData.description) === 'success' ? 'text-success' : 'text-error'}>
                                {formData.description.trim().length}/500 {t('createProject.characters')} ({t('createProject.errorMinLength', { min: 10 })})
                              </span>
                            ) : (
                              <span>{t('createProject.descriptionHelp')}</span>
                            )}
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="concept_details" className="form-label">
                            {t('createProject.conceptDetails')}
                            <i className="bi bi-info-circle help-icon" title={t('createProject.conceptDetailsHelp')}></i>
                          </label>
                          <textarea
                            id="concept_details"
                            name="concept_details"
                            className={`form-control ${getFieldValidationStatus('concept_details', formData.concept_details) === 'error'
                              ? 'form-control-error'
                              : ''
                              }`}
                            rows={4}
                            value={formData.concept_details}
                            onChange={handleInputChange}
                            disabled={uiState.loading || uiState.submitting}
                            placeholder={t('createProject.conceptDetailsPlaceholder')}
                            maxLength={1000}
                            aria-describedby={getFieldError('concept_details') ? 'concept-details-error' : undefined}
                          />
                          {getFieldError('concept_details') && (
                            <div id="concept-details-error" className="field-error" role="alert">
                              <i className="bi bi-exclamation-circle"></i>
                              {getFieldError('concept_details')}
                            </div>
                          )}
                          <div className="form-help">
                            {t('createProject.conceptDetailsCharCount', { count: formData.concept_details.length, max: 1000 })}
                          </div>
                        </div>

                        {/* Step completion indicator */}
                        {uiState.isBasicComplete && (
                          <div className="step-completion-indicator">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>{t('createProject.basicComplete')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 2: Project Details */}
                    <div className={`tab-pane ${uiState.activeTab === 'details' ? 'active' : ''}`}>
                      <div className="form-section">
                        <h2 className="section-title">
                          <i className="bi bi-sliders section-icon"></i>
                          {t('createProject.sectionProjectDetails')}
                        </h2>



                        {/* Key Features Section */}
                        <div className="form-group">
                          <label className="form-label">
                            {t('createProject.keyFeaturesRequired')}
                            <i className="bi bi-info-circle help-icon" title={t('createProject.keyFeaturesHelp')}></i>
                          </label>

                          <div className="key-features-list">
                            {formData.key_features.map((feature, index) => (
                              <div key={index} className="key-feature-item">
                                <div className="feature-input-group">
                                  <div className="feature-text-input">
                                    <input
                                      type="text"
                                      className={`form-control ${getFieldValidationStatus(`key_features[${index}]`, feature.text) === 'error'
                                        ? 'form-control-error'
                                        : getFieldValidationStatus(`key_features[${index}]`, feature.text) === 'success'
                                          ? 'form-control-success'
                                          : ''
                                        }`}
                                      value={feature.text}
                                      onChange={(e) => updateKeyFeature(index, e.target.value)}
                                      placeholder={t('createProject.featurePlaceholder')}
                                      disabled={uiState.loading || uiState.submitting}
                                      maxLength={200}
                                      aria-describedby={getFieldError(`key_features[${index}]`) ? `feature-${index}-error` : undefined}
                                    />
                                    {getFieldError(`key_features[${index}]`) && (
                                      <div id={`feature-${index}-error`} className="field-error" role="alert">
                                        <i className="bi bi-exclamation-circle"></i>
                                        {getFieldError(`key_features[${index}]`)}
                                      </div>
                                    )}
                                    <div className="form-help">
                                      {feature.text.length > 0 ? (
                                        <span className={getFieldValidationStatus(`key_features[${index}]`, feature.text) === 'success' ? 'text-success' : 'text-error'}>
                                          {feature.text.length}/200 {t('createProject.characters')}
                                        </span>
                                      ) : (
                                        <span>{t('createProject.featurePlaceholder')}</span>
                                      )}
                                    </div>
                                  </div>

                                  <div className="feature-actions">
                                    {formData.key_features.length > 1 && (
                                      <button
                                        type="button"
                                        className="btn-remove-feature"
                                        onClick={() => removeKeyFeature(index)}
                                        disabled={uiState.loading}
                                        title={t('createProject.removeFeature')}
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Screenshot Upload */}
                                <div className="feature-screenshot-upload">
                                  <label className="screenshot-upload-label">
                                    <i className="bi bi-camera"></i>
                                    {feature.screenshot 
                                      ? t('createProject.labelChangeScreenshot') 
                                      : t('createProject.labelAddScreenshot')}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0] || null;
                                        if (file) {
                                          const error = validateFile(file);
                                          if (error) {
                                            setUIState(prev => ({
                                              ...prev,
                                              fieldErrors: {
                                                ...prev.fieldErrors,
                                                [`key_features[${index}].screenshot`]: error
                                              }
                                            }));
                                            e.target.value = ''; // Clear the input
                                            return;
                                          } else {
                                            // Clear any previous file error
                                            setUIState(prev => ({
                                              ...prev,
                                              fieldErrors: {
                                                ...prev.fieldErrors,
                                                [`key_features[${index}].screenshot`]: ''
                                              }
                                            }));
                                          }
                                        }
                                        updateKeyFeatureScreenshot(index, file);
                                      }}
                                      disabled={uiState.loading}
                                      style={{ display: 'none' }}
                                    />
                                  </label>

                                  {getFieldError(`key_features[${index}].screenshot`) && (
                                    <div className="field-error" role="alert">
                                      <i className="bi bi-exclamation-circle"></i>
                                      {getFieldError(`key_features[${index}].screenshot`)}
                                    </div>
                                  )}

                                  {(feature.screenshot || feature.existingScreenshotUrl) && (
                                    <div className="screenshot-preview">
                                      <img
                                        src={feature.screenshot
                                          ? URL.createObjectURL(feature.screenshot)
                                          : feature.existingScreenshotUrl
                                        }
                                        alt={`Screenshot for ${feature.text || `Feature ${index + 1}`}`}
                                        className="screenshot-thumbnail"
                                        onError={(e) => {
                                          const target = e.target as HTMLImageElement;
                                          target.style.display = 'none';
                                          const errorMsg = target.nextElementSibling as HTMLElement;
                                          if (errorMsg && errorMsg.classList.contains('screenshot-error')) {
                                            errorMsg.style.display = 'block';
                                          }
                                        }}
                                      />
                                      <div className="screenshot-error" style={{ display: 'none' }}>
                                        <i className="bi bi-exclamation-triangle"></i>
                                        Unable to load screenshot
                                      </div>
                                      <div className="screenshot-info">
                                        <span className="screenshot-name">
                                          {feature.screenshot
                                            ? feature.screenshot.name
                                            : feature.existingScreenshotUrl
                                              ? t('createProject.labelExistingScreenshot')
                                              : t('createProject.labelScreenshot')
                                          }
                                        </span>
                                        {feature.screenshot && (
                                          <span className="screenshot-size">
                                            {(feature.screenshot.size / 1024).toFixed(1)} KB
                                          </span>
                                        )}
                                        {feature.existingScreenshotUrl && !feature.screenshot && (
                                          <span className="screenshot-status">
                                            <i className="bi bi-cloud-check"></i>
                                            Uploaded
                                          </span>
                                        )}
                                        <button
                                          type="button"
                                          className="btn-remove-screenshot"
                                          onClick={() => {
                                            // Clear both new file and existing URL
                                            setFormData(prev => ({
                                              ...prev,
                                              key_features: prev.key_features.map((f, i) =>
                                                i === index
                                                  ? { ...f, screenshot: null, existingScreenshotUrl: undefined }
                                                  : f
                                              )
                                            }));
                                            setUIState(prev => ({
                                              ...prev,
                                              hasUnsavedChanges: true,
                                              error: null,
                                              success: null
                                            }));
                                          }}
                                          disabled={uiState.loading}
                                          title={t('createProject.removeScreenshot')}
                                        >
                                          <i className="bi bi-x"></i>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>

                          <button
                            type="button"
                            className="btn-add-feature"
                            onClick={addKeyFeature}
                            disabled={uiState.loading || formData.key_features.length >= 10}
                            title={formData.key_features.length >= 10 ? t('createProject.errorMaxLength', { max: 10 }) : t('createProject.addFeature')}
                          >
                            <i className="bi bi-plus-circle"></i>
                            {t('createProject.addFeature')}
                            {formData.key_features.length >= 10 && (
                              <span className="max-features-note">({t('createProject.errorMaxLength', { max: 10 })})</span>
                            )}
                          </button>
                        </div>

                        {/* Pricing and Timeline Section */}
                        <div className="form-grid form-grid-2col">
                          <div className="form-group">
                            <label htmlFor="proposed_price" className="form-label">
                              Proposed Price (Optional)
                              <i className="bi bi-info-circle help-icon" title="What price are you considering for your project? This helps gauge market interest."></i>
                            </label>
                            <div className="price-input-group">
                              <span className="price-currency">$</span>
                              <input
                                type="number"
                                id="proposed_price"
                                name="proposed_price"
                                className={`form-control price-input ${getFieldValidationStatus('proposed_price', formData.proposed_price) === 'error'
                                  ? 'form-control-error'
                                  : getFieldValidationStatus('proposed_price', formData.proposed_price) === 'success'
                                    ? 'form-control-success'
                                    : ''
                                  }`}
                                value={formData.proposed_price}
                                onChange={handleInputChange}
                                disabled={uiState.loading || uiState.submitting}
                                placeholder={t('createProject.placeholderPrice')}
                                min="0"
                                step="0.01"
                                max="999999.99"
                                aria-describedby={getFieldError('proposed_price') ? 'price-error' : undefined}
                              />
                            </div>
                            {getFieldError('proposed_price') && (
                              <div id="price-error" className="field-error" role="alert">
                                <i className="bi bi-exclamation-circle"></i>
                                {getFieldError('proposed_price')}
                              </div>
                            )}
                            <div className="form-help">
                              {t('createProject.helpPriceBlank')}
                            </div>
                          </div>

                          <div className="form-group">
                            <label htmlFor="early_bird_discount" className="form-label">
                              Early Bird Discount (Optional)
                              <i className="bi bi-info-circle help-icon" title="Percentage discount for early supporters (e.g., 20 for 20% off)"></i>
                            </label>
                            <div className="discount-input-group">
                              <input
                                type="number"
                                id="early_bird_discount"
                                name="early_bird_discount"
                                className={`form-control discount-input ${getFieldValidationStatus('early_bird_discount', formData.early_bird_discount) === 'error'
                                  ? 'form-control-error'
                                  : getFieldValidationStatus('early_bird_discount', formData.early_bird_discount) === 'success'
                                    ? 'form-control-success'
                                    : ''
                                  }`}
                                value={formData.early_bird_discount}
                                onChange={handleInputChange}
                                disabled={uiState.loading || uiState.submitting}
                                placeholder={t('createProject.placeholderDiscount')}
                                min="0"
                                max="100"
                                step="1"
                                aria-describedby={getFieldError('early_bird_discount') ? 'discount-error' : undefined}
                              />
                              <span className="discount-percent">%</span>
                            </div>
                            {getFieldError('early_bird_discount') && (
                              <div id="discount-error" className="field-error" role="alert">
                                <i className="bi bi-exclamation-circle"></i>
                                {getFieldError('early_bird_discount')}
                              </div>
                            )}
                            <div className="form-help">
                              {t('createProject.helpDiscountPercentage')}
                            </div>
                          </div>
                        </div>

                        {/* Pricing Calculator Display */}
                        {pricingCalculation && (
                          <PricingCalculatorDisplay
                            calculation={pricingCalculation}
                            className="pricing-calculator-section"
                          />
                        )}

                        <div className="form-grid form-grid-2col">
                          <div className="form-group">
                            <label htmlFor="estimated_timeline" className="form-label">
                              {t('createProject.estimatedTimeline')}
                              <i className="bi bi-info-circle help-icon" title={t('createProject.estimatedTimelineHelp')}></i>
                            </label>
                            <select
                              id="estimated_timeline"
                              name="estimated_timeline"
                              className="form-select"
                              value={formData.estimated_timeline}
                              onChange={handleInputChange}
                              disabled={uiState.loading}
                            >
                              <option value="">{t('createProject.selectTimeline')}</option>
                              <option value="1-3 months">{t('createProject.timeline1to3')}</option>
                              <option value="3-6 months">{t('createProject.timeline3to6')}</option>
                              <option value="6-12 months">{t('createProject.timeline6to12')}</option>
                              <option value="1-2 years">{t('createProject.timeline1to2')}</option>
                              <option value="2+ years">{t('createProject.timeline2plus')}</option>
                              <option value="Already completed">{t('createProject.timelineCompleted')}</option>
                            </select>
                            <div className="form-help">
                              {t('createProject.estimatedTimelineHelp')}
                            </div>
                          </div>

                          <div className="form-group">
                            <label htmlFor="project_category" className="form-label">
                              {t('createProject.projectCategory')}
                              <i className="bi bi-info-circle help-icon" title={t('createProject.projectCategoryHelp')}></i>
                            </label>
                            <select
                              id="project_category"
                              name="project_category"
                              className="form-select"
                              value={formData.project_category}
                              onChange={handleInputChange}
                              disabled={uiState.loading}
                            >
                              <option value="">{t('createProject.selectCategory')}</option>
                              <option value="productivity">{t('createProject.categoryProductivity')}</option>
                              <option value="entertainment">{t('createProject.categoryEntertainment')}</option>
                              <option value="education">{t('createProject.categoryEducation')}</option>
                              <option value="health">{t('createProject.categoryHealth')}</option>
                              <option value="business">{t('createProject.categoryBusiness')}</option>
                              <option value="social">{t('createProject.categorySocial')}</option>
                              <option value="utilities">{t('createProject.categoryUtilities')}</option>
                              <option value="finance">{t('createProject.categoryFinance')}</option>
                              <option value="lifestyle">{t('createProject.categoryLifestyle')}</option>
                              <option value="other">{t('createProject.categoryOther')}</option>
                            </select>
                            <div className="form-help">
                              {t('createProject.projectCategoryHelp')}
                            </div>
                          </div>
                        </div>

                        {/* Step completion indicator */}
                        {uiState.isDetailsComplete && (
                          <div className="step-completion-indicator">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>{t('createProject.detailsComplete')}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Step 3: Media & Links */}
                    <div className={`tab-pane ${uiState.activeTab === 'media' ? 'active' : ''}`}>
                      <div className="form-section">
                        <h2 className="section-title">
                          <i className="bi bi-image section-icon"></i>
                          {t('createProject.mediaSectionTitle')}
                        </h2>

                        <div className="section-description">
                          <p>{t('createProject.mediaSectionDescription')}</p>
                        </div>



                        <div className="form-grid form-grid-2col">
                          <div className="form-group">
                            <label htmlFor="image_url" className="form-label">
                              {t('createProject.projectImageLabel')}
                              <i className="bi bi-info-circle help-icon" title={t('createProject.projectImageHelp')}></i>
                            </label>
                            <input
                              type="url"
                              id="image_url"
                              name="image_url"
                              className={`form-control ${getFieldValidationStatus('image_url', formData.image_url) === 'error'
                                ? 'form-control-error'
                                : getFieldValidationStatus('image_url', formData.image_url) === 'success'
                                  ? 'form-control-success'
                                  : ''
                                }`}
                              value={formData.image_url}
                              onChange={handleInputChange}
                              disabled={uiState.loading || uiState.submitting}
                              placeholder={t('createProject.projectImagePlaceholder')}
                              maxLength={500}
                              aria-describedby={getFieldError('image_url') ? 'image-url-error' : undefined}
                            />
                            {getFieldError('image_url') && (
                              <div id="image-url-error" className="field-error" role="alert">
                                <i className="bi bi-exclamation-circle"></i>
                                {getFieldError('image_url')}
                              </div>
                            )}
                            <div className="form-help">
                              {formData.image_url ? (
                                <span className={getFieldValidationStatus('image_url', formData.image_url) === 'success' ? 'text-success' : 'text-error'}>
                                  {getFieldValidationStatus('image_url', formData.image_url) === 'success'
                                    ? t('createProject.validUrlFormat')
                                    : t('createProject.invalidUrlFormat')
                                  }
                                </span>
                              ) : (
                                <span>{t('createProject.projectImageHelp')}</span>
                              )}
                            </div>

                            {/* Image preview */}
                            {formData.image_url && isValidUrl(formData.image_url) && (
                              <div className="url-preview">
                                <div className="preview-label">{t('createProject.previewLabel')}</div>
                                <div className="image-preview-container">
                                  <img
                                    src={formData.image_url}
                                    alt={t('createProject.labelProjectPreview')}
                                    className="image-preview"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                      const errorMsg = target.nextElementSibling as HTMLElement;
                                      if (errorMsg) errorMsg.style.display = 'block';
                                    }}
                                    onLoad={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'block';
                                      const errorMsg = target.nextElementSibling as HTMLElement;
                                      if (errorMsg) errorMsg.style.display = 'none';
                                    }}
                                  />
                                  <div className="image-error" style={{ display: 'none' }}>
                                    <i className="bi bi-exclamation-triangle"></i>
                                    {t('createProject.unableToLoadImage')}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="demo_url" className="form-label">
                              {t('createProject.demoUrlLabel')}
                              <i className="bi bi-info-circle help-icon" title={t('createProject.demoUrlHelp')}></i>
                            </label>
                            <input
                              type="url"
                              id="demo_url"
                              name="demo_url"
                              className={`form-control ${getFieldValidationStatus('demo_url', formData.demo_url) === 'error'
                                ? 'form-control-error'
                                : getFieldValidationStatus('demo_url', formData.demo_url) === 'success'
                                  ? 'form-control-success'
                                  : ''
                                }`}
                              value={formData.demo_url}
                              onChange={handleInputChange}
                              disabled={uiState.loading || uiState.submitting}
                              placeholder={t('createProject.demoUrlPlaceholder')}
                              maxLength={500}
                              aria-describedby={getFieldError('demo_url') ? 'demo-url-error' : undefined}
                            />
                            {getFieldError('demo_url') && (
                              <div id="demo-url-error" className="field-error" role="alert">
                                <i className="bi bi-exclamation-circle"></i>
                                {getFieldError('demo_url')}
                              </div>
                            )}
                            <div className="form-help">
                              {formData.demo_url ? (
                                <span className={getFieldValidationStatus('demo_url', formData.demo_url) === 'success' ? 'text-success' : 'text-error'}>
                                  {getFieldValidationStatus('demo_url', formData.demo_url) === 'success'
                                    ? t('createProject.validUrlFormat')
                                    : t('createProject.invalidUrlFormat')
                                  }
                                </span>
                              ) : (
                                <span>{t('createProject.demoUrlHelp')}</span>
                              )}
                            </div>

                            {/* Demo link preview */}
                            {formData.demo_url && isValidUrl(formData.demo_url) && (
                              <div className="url-preview">
                                <div className="preview-label">{t('createProject.demoLinkLabel')}</div>
                                <div className="link-preview">
                                  <a
                                    href={formData.demo_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="demo-link"
                                  >
                                    <i className="bi bi-box-arrow-up-right"></i>
                                    {formData.demo_url.length > 50
                                      ? `${formData.demo_url.substring(0, 47)}...`
                                      : formData.demo_url
                                    }
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Media & Links Tips */}
                        <div className="form-tips">
                          <h4 className="tips-title">
                            <i className="bi bi-lightbulb"></i>
                            {t('createProject.tipsMediaTitle')}
                          </h4>
                          <ul className="tips-list">
                            <li>{t('createProject.tipProjectImage')}</li>
                            <li>{t('createProject.tipDemoUrl')}</li>
                            <li>{t('createProject.tipImageHosting')}</li>
                            <li>{t('createProject.tipMobileFriendly')}</li>
                          </ul>
                        </div>

                        {/* Step completion indicator */}
                        {uiState.isMediaComplete && (
                          <div className="step-completion-indicator">
                            <i className="bi bi-check-circle-fill"></i>
                            <span>{t('createProject.mediaComplete')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="form-actions">
                    <div>
                      {uiState.activeTab !== 'basic' && (
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => {
                            if (uiState.activeTab === 'details') tabNavigation('basic');
                            else if (uiState.activeTab === 'media') tabNavigation('details');
                          }}
                        >
                          <i className="bi bi-arrow-left"></i>
                          {t('common.previous')}
                        </button>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                      {uiState.activeTab !== 'media' ? (
                        <button
                          type="button"
                          className={`btn-primary ${(uiState.activeTab === 'basic' && !uiState.isBasicComplete) ||
                            (uiState.activeTab === 'details' && !uiState.isDetailsComplete)
                            ? 'btn-disabled'
                            : ''
                            }`}
                          onClick={(e) => {
                            e.preventDefault();
                            if (uiState.activeTab === 'basic') {
                              if (uiState.isBasicComplete) {
                                tabNavigation('details');
                              } else {
                                // Show specific validation errors
                                const errors = getBasicStepErrors();
                                setUIState(prev => ({
                                  ...prev,
                                  error: t('createProject.fixFollowingIssues', { issues: errors.map(e => e.message).join(', ') }),
                                  showValidationSummary: true
                                }));
                              }
                            } else if (uiState.activeTab === 'details') {
                              if (uiState.isDetailsComplete) {
                                tabNavigation('media');
                              } else {
                                // Show specific validation errors
                                const errors = getDetailsStepErrors();
                                setUIState(prev => ({
                                  ...prev,
                                  error: t('createProject.fixFollowingIssues', { issues: errors.map(e => e.message).join(', ') }),
                                  showValidationSummary: true
                                }));
                              }
                            }
                          }}
                          disabled={uiState.loading || uiState.submitting}
                          title={
                            uiState.activeTab === 'basic' && !uiState.isBasicComplete
                              ? t('createProject.completeRequiredFields')
                              : uiState.activeTab === 'details' && !uiState.isDetailsComplete
                                ? t('createProject.addKeyFeaturesToProceed')
                                : t('common.next')
                          }
                        >
                          {uiState.activeTab === 'basic' && !uiState.isBasicComplete ? (
                            <>
                              <i className="bi bi-exclamation-triangle"></i>
                              {t('createProject.completeRequiredFieldsButton')}
                            </>
                          ) : uiState.activeTab === 'details' && !uiState.isDetailsComplete ? (
                            <>
                              <i className="bi bi-exclamation-triangle"></i>
                              {t('createProject.addKeyFeaturesButton')}
                            </>
                          ) : (
                            <>
                              {t('common.next')}
                              <i className="bi bi-arrow-right"></i>
                            </>
                          )}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className={`btn-primary ${!uiState.isMediaComplete ? 'btn-disabled' : ''
                            }`}
                          disabled={
                            uiState.loading ||
                            uiState.submitting ||
                            uiState.uploadingFiles ||
                            !uiState.isBasicComplete ||
                            !uiState.isDetailsComplete ||
                            !uiState.isMediaComplete ||
                            (isEditMode && !actualChanges)
                          }
                          title={
                            !uiState.isMediaComplete
                              ? t('createProject.fixUrlValidationErrors')
                              : isEditMode && !actualChanges
                                ? t('createProject.noChangesToSave')
                                : isEditMode
                                  ? t('createProject.saveChanges')
                                  : t('createProject.createProjectButton')
                          }
                        >
                          {/* uiState.loading || uiState.submitting ? (
                        <>
                          <i className="bi bi-hourglass-split"></i>
                          {uiState.uploadingFiles 
                            ? 'Uploading files...' 
                            : isEditMode 
                              ? 'Saving...' 
                              : 'Creating...'
                          }
                        </>
                      ) : */ !uiState.isMediaComplete ? (
                              <>
                                <i className="bi bi-exclamation-triangle"></i>
                                {t('createProject.fixUrlErrors')}
                              </>
                            ) : (
                              <>
                                <i className={`bi ${isEditMode ? 'bi-check-circle' : 'bi-plus-circle'}`}></i>
                                {submitButtonText}
                              </>
                            )}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {uiState.confirmDialog && (
        <ProjectUpdateConfirmDialog
          isOpen={uiState.confirmDialog.isOpen}
          type={uiState.confirmDialog.type}
          title={uiState.confirmDialog.title}
          message={uiState.confirmDialog.message}
          details={uiState.confirmDialog.details}
          confirmText={uiState.confirmDialog.confirmText}
          cancelText={uiState.confirmDialog.cancelText}
          onConfirm={uiState.confirmDialog.onConfirm}
          onCancel={hideConfirmDialog}
          loading={uiState.loading}
          warningLevel={uiState.confirmDialog.warningLevel}
        />
      )}
    </BasePageLayout>
  );
};

export default CreatePreReleaseProjectPage;